<template>
  <div class="login-page">
    <!-- Background decor -->
    <div class="login-page__blob login-page__blob--1" aria-hidden="true" />
    <div class="login-page__blob login-page__blob--2" aria-hidden="true" />
    <div class="login-page__blob login-page__blob--3" aria-hidden="true" />

    <main class="login-page__main">
      <div class="login-page__inner">
        <header class="login-page__brand">
          <span class="login-page__logo-wrap">
            <img
              class="login-page__logo-img"
              :src="logoSrc"
              :alt="logoAlt"
            />
          </span>
          <h1 class="login-page__title">
            <span class="login-page__title-bold">{{ brand.bold }}</span>
            <span class="login-page__title-light">{{ brand.normal }}</span>
          </h1>
          <p class="login-page__subtitle">{{ t("subtitleContinue") }}</p>
        </header>

        <div class="login-page__card">
          <div v-if="loginError" class="login-page__error login-page__error--card" role="alert">
            {{ loginError }}
          </div>

          <div
            v-if="isIdpAuthenticated"
            class="login-page__idp-session"
          >
            <img
              v-if="idpSessionAvatar"
              class="login-page__idp-avatar"
              :src="idpSessionAvatar"
              :alt="idpSessionName"
            />
            <p v-if="idpSessionName" class="login-page__idp-name">{{ idpSessionName }}</p>
            <button
              type="button"
              class="login-page__submit login-page__submit--sso"
              :disabled="ssoLoading || loading"
              @click="loginWithSso(true)"
            >
              <svg
                v-if="ssoLoading"
                class="login-page__spinner"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <span>{{
                ssoLoading
                  ? t("ssoRedirecting")
                  : idpSessionName
                    ? t("ssoContinueWith").replace("{name}", idpSessionName)
                    : t("ssoContinue")
              }}</span>
            </button>
          </div>

          <div
            v-else-if="showSsoOnlyEntry"
            class="login-page__sso-only"
          >
            <button
              type="button"
              class="login-page__submit login-page__submit--sso"
              :disabled="ssoLoading"
              @click="loginWithSso(false)"
            >
              <svg
                v-if="ssoLoading"
                class="login-page__spinner"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <span>{{ ssoLoading ? t("ssoRedirecting") : t("ssoLogin") }}</span>
            </button>
          </div>

          <form
            v-if="showLegacyLoginForm"
            class="login-page__form"
            @submit.prevent="login"
          >

            <div class="login-page__field">
              <label for="login-username">{{ t("username") }}</label>
              <input
                id="login-username"
                ref="usernameInput"
                v-model="username"
                type="text"
                class="login-page__input"
                placeholder="username"
                autocomplete="username"
                @input="loginError = ''"
              />
              <validation-error :errors="apiValidationErrors.username" />
            </div>

            <div class="login-page__field">
              <label for="login-password">{{ t("password") }}</label>
              <div class="login-page__password-wrap">
                <input
                  id="login-password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="login-page__input login-page__input--password"
                  placeholder="********"
                  autocomplete="current-password"
                  @input="loginError = ''"
                />
                <button
                  type="button"
                  class="login-page__toggle-pw"
                  tabindex="-1"
                  :aria-label="showPassword ? t('hidePassword') : t('showPassword')"
                  @click="showPassword = !showPassword"
                >
                  <svg
                    v-if="!showPassword"
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                    <line x1="2" x2="22" y1="2" y2="22" />
                  </svg>
                </button>
              </div>
              <validation-error :errors="apiValidationErrors.password" />
            </div>

            <button
              type="submit"
              class="login-page__submit"
              :disabled="submitDisabled"
            >
              <svg
                v-if="loading"
                class="login-page__spinner"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <span>{{ loading ? t("submitting") : t("submit") }}</span>
            </button>

            <template v-if="showSsoLoginButton && !isIdpAuthenticated">
              <div class="login-page__divider" role="separator">
                <span>{{ t("orDivider") }}</span>
              </div>
              <button
                type="button"
                class="login-page__submit login-page__submit--sso"
                :disabled="ssoLoading || loading"
                @click="loginWithSso(false)"
              >
                <svg
                  v-if="ssoLoading"
                  class="login-page__spinner"
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  aria-hidden="true"
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                <span>{{
                  ssoLoading ? t("ssoRedirecting") : t("ssoLogin")
                }}</span>
              </button>
            </template>
          </form>
        </div>
      </div>
    </main>

    <footer class="login-page__footer">
      <div v-if="showLanguageSwitcher" class="login-page__lang">
        <button
          type="button"
          class="login-page__lang-btn"
          :class="{ 'login-page__lang-btn--active': locale === 'vi-VN' }"
          aria-label="Tiếng Việt"
          @click="setLocale('vi-VN')"
        >
          <svg class="login-page__flag" viewBox="0 0 640 480" aria-hidden="true">
            <rect width="640" height="480" fill="#da251d" />
            <polygon fill="#ff0" points="320,96 336.6,152.2 396.8,152.2 347.1,188.1 363.7,244.3 320,208.4 276.3,244.3 292.9,188.1 243.2,152.2 303.4,152.2" />
          </svg>
        </button>
        <span class="login-page__lang-divider" aria-hidden="true" />
        <button
          type="button"
          class="login-page__lang-btn"
          :class="{ 'login-page__lang-btn--active': locale === 'en-US' }"
          aria-label="English"
          @click="setLocale('en-US')"
        >
          <svg class="login-page__flag" viewBox="0 0 640 480" aria-hidden="true">
            <path fill="#012169" d="M0 0h640v480H0z" />
            <path fill="#FFF" d="m75 0 244 181L181 0h75L0 150l75 330h75L320 301l170 179h75L565 330 640 150 475 0h-75L320 181 75 0z" />
            <path fill="#C8102E" d="m424 281 216 199v-40L369 281h55zm-184 20 6 35L54 480h40l156-179h-10zM640 0 320 181 75 0h40l205 150L475 0h165zM0 480l240-179-6-35L0 319v161zm320-301 184 221h-55L320 301 136 480h55l129-151z" />
          </svg>
        </button>
      </div>
      <p class="login-page__copyright">
        © {{ year }} {{ brand.bold }} {{ brand.normal }}. {{ t("copyright") }}
      </p>
    </footer>
  </div>
</template>

<script>
import { ValidationError } from "@/components";
import formMixin from "@/mixins/form-mixin";
import { parseLoginBrand, parseAgencyBranchBrand } from "../../../helper/loginBrand";
import { getLoginLocale, setLoginLocale, loginT } from "../../../helper/loginI18n";
import {
  loadSsoConfig,
  openSsoPopup,
  installSsoPopupMessageListener,
  fetchAuthMe,
  fetchIdpSessionHint,
  watchSsoPopupUntilClosed,
  ssoErrorFromRoute,
  isLegacyRedirectOnly,
} from "../../../helper/sso";
import {
  agencyBranchConfigUrl,
  isAgencySocialSite,
} from "@/config/agencyRuntime";
import axios from "axios";

const LAST_USERNAME_KEY = "last_username";

const SSO_ERROR_MESSAGES = {
  access_denied: "Bạn không có quyền truy cập chi nhánh này.",
  login_required: "Vui lòng đăng nhập lại.",
  missing_code: "Phiên SSO không hợp lệ. Thử đăng nhập lại.",
  unknown_tenant: "Không xác định được chi nhánh từ domain.",
  token_exchange_failed: "Không đổi được token SSO. Thử lại.",
  invalid_state: "Phiên SSO không hợp lệ. Thử đăng nhập lại.",
  popup_blocked: "Trình duyệt đã chặn cửa sổ đăng nhập. Cho phép popup và thử lại.",
};

export default {
  name: "LoginPage",
  components: { ValidationError },
  mixins: [formMixin],

  data() {
    return {
      username: "",
      password: "",
      showPassword: false,
      loading: false,
      loginError: "",
      locale: getLoginLocale(),
      year: new Date().getFullYear(),
      /** Tạm ẩn chuyển ngôn ngữ trên màn login */
      showLanguageSwitcher: false,
      ssoConfig: null,
      ssoLoading: false,
      idpSession: null,
      removeSsoPopupListener: null,
      ssoPopupWatcher: null,
      removeIdpSessionRefresh: null,
      /** Agency site: brand từ branch-config (logo vẫn dùng favicon social chung) */
      agencyBrand: null,
    };
  },

  computed: {
    showSsoLoginButton() {
      const cfg = this.ssoConfig;
      return !!(cfg && cfg.enabled && cfg.legacy_login !== false);
    },
    showLegacyLoginForm() {
      const cfg = this.ssoConfig;
      if (!cfg || !cfg.enabled) {
        return true;
      }
      return cfg.legacy_login !== false;
    },
    showSsoOnlyEntry() {
      const cfg = this.ssoConfig;
      return !!(cfg && cfg.enabled && cfg.legacy_login === false && !this.isIdpAuthenticated);
    },
    isIdpAuthenticated() {
      return !!(this.idpSession && this.idpSession.status === "authenticated");
    },
    idpSessionName() {
      const d = this.idpSession && this.idpSession.data;
      if (!d || Array.isArray(d)) {
        return "";
      }
      return d.name ? String(d.name) : "";
    },
    idpSessionAvatar() {
      const d = this.idpSession && this.idpSession.data;
      if (!d || Array.isArray(d)) {
        return "";
      }
      const url = d.avatar_url || d.avatar;
      return url ? String(url) : "";
    },
    brand() {
      if (this.agencyBrand) {
        return this.agencyBrand;
      }
      const host =
        typeof window !== "undefined" ? window.location.hostname : "";
      return parseLoginBrand(host);
    },
    isAgencyLogin() {
      return isAgencySocialSite();
    },
    submitDisabled() {
      return (
        this.loading ||
        !String(this.username || "").trim() ||
        !String(this.password || "")
      );
    },
    logoSrc() {
      const base = process.env.VUE_APP_BASE_URL || "";
      return `${base}/favicon-96x96.png`;
    },
    logoAlt() {
      return `${this.brand.bold} ${this.brand.normal}`.trim();
    },
  },

  async created() {
    if (this.isAgencyLogin) {
      await this.loadAgencyBranchBrand();
    }

    const ssoErr = ssoErrorFromRoute();
    if (ssoErr) {
      this.loginError =
        SSO_ERROR_MESSAGES[ssoErr] ||
        "Đăng nhập SSO thất bại. Vui lòng thử lại.";
    }

    const cfg = await loadSsoConfig();
    this.ssoConfig = cfg;

    if (isLegacyRedirectOnly(cfg)) {
      this.$router.replace({ name: "LegacySocialRedirect" });
      return;
    }

    if (cfg && cfg.enabled) {
      if (await fetchAuthMe()) {
        try {
          const boot = await this.$store.dispatch("bootstrapSso");
          if (boot && boot.status) {
            return;
          }
        } catch {
          /* fall through */
        }
      }

      this.idpSession = await fetchIdpSessionHint(cfg);
    }

    this.restoreRememberedUsername();
    this.clearStaleLocalToken();
  },

  mounted() {
    localStorage.setItem("router_name", this.$route.name);
    this.syncDocumentTitle();
    this.removeSsoPopupListener = installSsoPopupMessageListener({
      onSuccess: (redirect) => this.handleSsoPopupSuccess(redirect),
      onError: (error) => this.handleSsoPopupError(error),
    });
    this.removeIdpSessionRefresh = this.installIdpSessionRefresh();
    this.$nextTick(() => {
      const el = this.$refs.usernameInput;
      if (el && typeof el.focus === "function") {
        el.focus();
      }
    });
  },

  beforeDestroy() {
    this.clearSsoPopupWatcher();
    if (typeof this.removeIdpSessionRefresh === "function") {
      this.removeIdpSessionRefresh();
      this.removeIdpSessionRefresh = null;
    }
    if (typeof this.removeSsoPopupListener === "function") {
      this.removeSsoPopupListener();
      this.removeSsoPopupListener = null;
    }
  },

  watch: {
    brand: {
      handler() {
        this.syncDocumentTitle();
      },
      deep: true,
    },
  },

  methods: {
    async refreshIdpSessionHint() {
      if (!this.ssoConfig?.enabled) {
        return;
      }
      try {
        this.idpSession = await fetchIdpSessionHint(this.ssoConfig);
      } catch {
        this.idpSession = { status: "unauthenticated" };
      }
    },
    installIdpSessionRefresh() {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return () => undefined;
      }
      let pending = false;
      const onRefresh = () => {
        if (pending || document.visibilityState === "hidden") {
          return;
        }
        pending = true;
        this.refreshIdpSessionHint().finally(() => {
          pending = false;
        });
      };
      document.addEventListener("visibilitychange", onRefresh);
      window.addEventListener("focus", onRefresh);
      return () => {
        document.removeEventListener("visibilitychange", onRefresh);
        window.removeEventListener("focus", onRefresh);
      };
    },
    syncDocumentTitle() {
      if (typeof document === "undefined") {
        return;
      }
      const { bold, normal } = this.brand;
      document.title = [bold, normal].filter(Boolean).join(" ").trim();
    },
    async loadAgencyBranchBrand() {
      const url = agencyBranchConfigUrl();
      if (!url) {
        return;
      }
      try {
        const { data } = await axios.get(url, {
          withCredentials: false,
          headers: {
            Accept: "application/json",
            ...(typeof window !== "undefined" && window.location.hostname
              ? { "X-Social-Frontend-Host": window.location.hostname }
              : {}),
          },
        });
        if (data && data.status && data.data) {
          this.agencyBrand = parseAgencyBranchBrand(data.data);
          this.syncDocumentTitle();
        }
      } catch {
        /* fallback parseLoginBrand từ hostname */
      }
    },
    t(key) {
      return loginT(this.locale, key);
    },
    setLocale(loc) {
      setLoginLocale(loc);
      this.locale = loc;
    },
    restoreRememberedUsername() {
      const saved = localStorage.getItem(LAST_USERNAME_KEY);
      if (saved) {
        this.username = saved;
      }
    },
    clearStaleLocalToken() {
      const hasUser = !!(this.$store.state.auth.user && this.$store.state.auth.user.id);
      if (localStorage.getItem("access_token") && !hasUser) {
        import("../../../helper/sessionRedirect").then(({ clearClientSession }) => {
          clearClientSession();
          this.$store.commit("isAuthenticated", { isAuthenticated: false });
          import("../../../helper/sso").then((m) => m.resetSsoConfigCache()).catch(() => undefined);
        });
      }
    },
    async login() {
      this.loginError = "";
      this.resetApiValidation();
      this.loading = true;
      const trimmedUser = String(this.username || "").trim();
      try {
        const data = await this.$store.dispatch("login", {
          username: trimmedUser,
          password: this.password,
        });
        if (data && data.status) {
          localStorage.setItem(LAST_USERNAME_KEY, trimmedUser);
          return;
        }
        const msg =
          (data && data.message) ||
          "Tên tài khoản hoặc mật khẩu không đúng!";
        this.loginError = msg;
        if (data && data.data) {
          this.setApiValidation(data.data);
        }
      } catch (e) {
        this.loginError = "Tên tài khoản hoặc mật khẩu không đúng!";
      } finally {
        this.loading = false;
      }
    },
    loginWithSso(idpAuthenticated = false) {
      this.loginError = "";
      this.clearSsoPopupWatcher();
      this.ssoLoading = true;
      const popup = openSsoPopup(this.ssoConfig, idpAuthenticated);
      if (!popup) {
        this.ssoLoading = false;
        this.loginError = SSO_ERROR_MESSAGES.popup_blocked;
        return;
      }
      this.ssoPopupWatcher = watchSsoPopupUntilClosed(popup, () => {
        this.handleSsoPopupClosed();
      });
    },
    clearSsoPopupWatcher() {
      if (this.ssoPopupWatcher && typeof this.ssoPopupWatcher.stop === "function") {
        this.ssoPopupWatcher.stop();
      }
      this.ssoPopupWatcher = null;
    },
    markSsoPopupSettled() {
      if (this.ssoPopupWatcher && typeof this.ssoPopupWatcher.markSettled === "function") {
        this.ssoPopupWatcher.markSettled();
      }
      this.ssoPopupWatcher = null;
    },
    async handleSsoPopupClosed() {
      this.ssoLoading = false;
      this.ssoPopupWatcher = null;
      await this.refreshIdpSessionHint();
    },
    async handleSsoPopupSuccess(redirect) {
      this.markSsoPopupSettled();
      this.ssoLoading = true;
      this.loginError = "";
      try {
        const boot = await this.$store.dispatch("bootstrapSso");
        if (boot && boot.status) {
          return;
        }
        if (redirect && this.$router) {
          this.$router.replace(redirect).catch(() => undefined);
        }
      } catch {
        this.loginError = "Đăng nhập SSO thất bại. Vui lòng thử lại.";
      } finally {
        this.ssoLoading = false;
      }
    },
    handleSsoPopupError(error) {
      this.markSsoPopupSettled();
      this.ssoLoading = false;
      this.loginError =
        SSO_ERROR_MESSAGES[error] ||
        "Đăng nhập SSO thất bại. Vui lòng thử lại.";
    },
  },
};
</script>

<style scoped lang="scss">
/* Design tokens (login) */
.login-page {
  --login-primary: oklch(0.511 0.262 276.966);
  --login-primary-rgb: 124, 58, 237;
  --login-destructive: #dc2626;
  --login-card-bg: rgba(255, 255, 255, 0.5);
  --login-muted: rgba(15, 23, 42, 0.55);
  --login-radius: 0.625rem;

  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100svh;
  overflow: hidden;
  background: #f8fafc; /* slate-50 */
  pointer-events: auto;
}

.login-page__blob {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(64px);
}

.login-page__blob--1 {
  width: 24rem;
  height: 24rem;
  left: -5rem;
  top: -5rem;
  background: rgba(var(--login-primary-rgb), 0.1);
}

.login-page__blob--2 {
  width: 30rem;
  height: 30rem;
  right: -5rem;
  bottom: -8rem;
  background: rgba(59, 130, 246, 0.1);
}

.login-page__blob--3 {
  width: 20rem;
  height: 20rem;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: rgba(99, 102, 241, 0.05);
}

.login-page__main {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
}

.login-page__inner {
  width: 100%;
  max-width: 28rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.login-page__brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.login-page__logo-wrap {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 0.5rem;
  overflow: visible;
  background: rgba(255, 255, 255, 0.4);
  box-shadow:
    0 1px 2px 0 rgba(15, 23, 42, 0.06),
    0 0 0 1px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
    margin-bottom: 16px;
}

.login-page__logo-img {
  width: 48px;
  height: 48px;
  object-fit: contain;
  display: block;
}

.login-page__title {
  margin: 0;
  font-size: 2.25rem;
  font-weight: 900;
  letter-spacing: -0.04em;
  line-height: 1.1;
  background: linear-gradient(90deg, #9333ea, #4f46e5, #2563eb);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.login-page__title-bold {
  font-weight: 900;
}

.login-page__title-light {
  font-weight: 300;
  margin-left: 0.2em;
}

.login-page__subtitle {
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  color: rgba(15, 23, 42, 0.8);
}

.login-page__card {
  border-radius: 1rem;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: var(--login-card-bg);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  padding: 2rem;
  box-shadow:
    0 25px 50px -12px rgba(var(--login-primary-rgb), 0.05),
    0 25px 50px -12px rgba(15, 23, 42, 0.12);
}

.login-page__form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.login-page__error {
  border-radius: var(--login-radius);
  background: rgba(220, 38, 38, 0.1);
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--login-destructive);
  animation: login-error-in 0.35s ease-out;
}

.login-page__error--card {
  margin-bottom: 1rem;
}

.login-page__sso-only {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

@keyframes login-error-in {
  from {
    opacity: 0;
    transform: translateY(-0.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-page__field label {
  display: block;
  font-size: 0.875rem;
  color: rgba(15, 23, 42, 0.7);
  margin-bottom: 0.25rem;
}

.login-page__input {
  box-sizing: border-box;
  width: 100%;
  height: 2.25rem;
  margin-top: 0.25rem;
  padding: 0 0.75rem;
  border-radius: var(--login-radius);
  border: 1px solid rgba(15, 23, 42, 0.2);
  background: rgba(255, 255, 255, 0.5);
  font-size: 0.875rem;
  color: #0f172a;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}

.login-page__input:focus {
  border-color: var(--login-primary);
  background: #fff;
  box-shadow: 0 0 0 3px rgba(var(--login-primary-rgb), 0.18);
}

.login-page__input--password {
  padding-right: 2.5rem;
}

.login-page__password-wrap {
  position: relative;
  margin-top: 0.25rem;
}

.login-page__toggle-pw {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border: 0;
  background: transparent;
  color: var(--login-muted);
  cursor: pointer;
}

.login-page__toggle-pw:hover {
  color: var(--login-primary);
}

.login-page__submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  height: 2.25rem;
  margin-top: 0.25rem;
  border: 0;
  border-radius: var(--login-radius);
  font-size: 0.875rem;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  background: var(--login-primary);
  box-shadow: 0 10px 15px -3px rgba(var(--login-primary-rgb), 0.25);
  transition: filter 0.15s, transform 0.15s, opacity 0.15s;
}

.login-page__submit:hover:not(:disabled) {
  filter: brightness(1.06);
}

.login-page__submit:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.login-page__submit--sso {
  color: var(--login-primary);
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(var(--login-primary-rgb), 0.35);
  box-shadow: none;
}

.login-page__submit--sso:hover:not(:disabled) {
  filter: none;
  background: #fff;
  border-color: var(--login-primary);
  box-shadow: 0 0 0 3px rgba(var(--login-primary-rgb), 0.12);
}

.login-page__idp-session {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 0.25rem 0 0.5rem;
}

.login-page__idp-avatar {
  width: 4rem;
  height: 4rem;
  border-radius: 9999px;
  object-fit: cover;
  border: 2px solid rgba(var(--login-primary-rgb), 0.25);
}

.login-page__idp-name {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  text-align: center;
}

.login-page__divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.25rem 0;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--login-muted);
  text-transform: lowercase;
}

.login-page__divider::before,
.login-page__divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: rgba(15, 23, 42, 0.12);
}

.login-page__spinner {
  animation: login-spin 0.8s linear infinite;
}

@keyframes login-spin {
  to {
    transform: rotate(360deg);
  }
}

.login-page__footer {
  position: relative;
  z-index: 1;
  margin-top: auto;
  padding: 0.5rem 1rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  max-width: 28rem;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.login-page__lang {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.login-page__lang-btn {
  display: inline-flex;
  padding: 0.15rem;
  border: 0;
  background: transparent;
  cursor: pointer;
  opacity: 0.4;
  transition: opacity 0.15s;
}

.login-page__lang-btn--active,
.login-page__lang-btn:hover {
  opacity: 1;
}

.login-page__lang-divider {
  width: 1px;
  height: 1rem;
  background: rgba(15, 23, 42, 0.2);
}

.login-page__flag {
  width: 1.5rem;
  height: auto;
  display: block;
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.08);
}

.login-page__copyright {
  margin: 0;
  font-size: 0.75rem;
  color: rgba(15, 23, 42, 0.45);
  text-align: center;
}
</style>
